interface LocaleConfig {
    reviewsSeparator: string;
    ratingSeparator: string;
    thousands: { single: string; plural: string };
    millions: { single: string; plural: string };
}

const LOCALE_CONFIGS: Record<string, LocaleConfig> = {
    'en-US': {
        reviewsSeparator: '.',
        ratingSeparator: '.',
        thousands: { single: 'K', plural: 'K' },
        millions: { single: 'M', plural: 'M' },
    },
    'pt-BR': {
        reviewsSeparator: ',',
        ratingSeparator: ',',
        thousands: { single: ' mil', plural: ' mil' },
        millions: { single: ' mi', plural: ' mi' },
    },
    'de-DE': {
        reviewsSeparator: ',',
        ratingSeparator: ',',
        thousands: { single: ' Tsd.', plural: ' Tsd.' },
        millions: { single: ' Mio.', plural: ' Mio.' },
    },
    'fr-FR': {
        reviewsSeparator: '.',
        ratingSeparator: ',',
        thousands: { single: 'k', plural: 'k' },
        millions: { single: 'M', plural: 'M' },
    },
    'nl-NL': {
        reviewsSeparator: ',',
        ratingSeparator: ',',
        thousands: { single: 'k', plural: 'k' },
        millions: { single: ' mln.', plural: ' mln.' },
    },
    es: {
        reviewsSeparator: ',',
        ratingSeparator: ',',
        thousands: { single: 'K', plural: 'K' },
        millions: { single: 'M', plural: 'M' },
    },
    'it-IT': {
        reviewsSeparator: ',',
        ratingSeparator: ',',
        thousands: { single: ' mille', plural: ' mila' },
        millions: { single: ' milione', plural: ' milioni' },
    },
};

export const formatRating = (num: number, locale?: string): string => {
    const config = getLocaleConfig(locale);
    const formattedNumber = num.toFixed(1);

    return formattedNumber.replace('.', config.ratingSeparator);
};

const getLocaleConfig = (locale?: string): LocaleConfig => {
    if (!locale) {
        return LOCALE_CONFIGS['en-US'];
    }

    return LOCALE_CONFIGS[locale] || LOCALE_CONFIGS['en-US'];
};

const getThousandsNotation = (value: number, config: LocaleConfig): string => {
    if (config.thousands.single !== config.thousands.plural) {
        const thousands = Math.floor(value / 1000);
        const remainder = value % 1000;

        if (thousands === 1 && remainder === 0) {
            return config.thousands.single;
        }
    }
    return config.thousands.plural;
};

const getMillionsNotation = (value: number, config: LocaleConfig): string => {
    if (config.millions.single !== config.millions.plural) {
        const millions = value / 1000000;

        if (millions >= 1 && millions < 2) {
            return config.millions.single;
        }
    }
    return config.millions.plural;
};

const roundToSignificantDigits = (value: number, digits: number): number => {
    if (value === 0) return 0;
    const magnitude = Math.floor(Math.log10(Math.abs(value)));
    const scale = Math.pow(10, magnitude - digits + 1);
    return Math.floor(value / scale) * scale;
};

interface FormatStrategy {
    maxValue: number;
    format: (rounded: number, config: LocaleConfig) => string;
    significantDigits: number;
}

const formatStrategies: FormatStrategy[] = [
    {
        maxValue: 100,
        significantDigits: 1,
        format: (rounded) => `${Math.ceil(rounded / 100) * 100}`,
    },
    {
        maxValue: 901,
        significantDigits: 2,
        format: (rounded) => `${Math.ceil(rounded / 100) * 100}`,
    },
    {
        maxValue: 1000,
        significantDigits: 2,
        format: (_rounded, config) => {
            const kNotation = getThousandsNotation(1000, config);

            return `1${kNotation}`;
        },
    },
    {
        maxValue: 10000,
        significantDigits: 2,
        format: (rounded, config) => {
            const thousands = Math.floor(rounded / 1000);
            const hundreds = Math.floor((rounded % 1000) / 100);
            const kNotation = getThousandsNotation(rounded, config);

            if (hundreds > 0) {
                return `${thousands}${config.reviewsSeparator}${hundreds}${kNotation}`;
            }

            return `${thousands}${kNotation}`;
        },
    },
    {
        maxValue: 1000000,
        significantDigits: 2,
        format: (rounded, config) => {
            const thousands = Math.floor(rounded / 1000);
            const kNotation = getThousandsNotation(rounded, config);

            return `${thousands}${kNotation}`;
        },
    },
    {
        maxValue: 10000000,
        significantDigits: 2,
        format: (rounded, config) => {
            const millions = Math.floor(rounded / 1000000);
            const hundredThousands = Math.floor((rounded % 1000000) / 100000);
            const mNotation = getMillionsNotation(rounded, config);

            if (hundredThousands > 0) {
                return `${millions}${config.reviewsSeparator}${hundredThousands}${mNotation}`;
            }

            return `${millions}${mNotation}`;
        },
    },
    {
        maxValue: Infinity,
        significantDigits: 2,
        format: (rounded, config) => {
            const millions = Math.floor(rounded / 1000000);
            const mNotation = getMillionsNotation(rounded, config);

            return `${millions}${mNotation}`;
        },
    },
];

export const formatReviewsWithApprox = (num: number, locale?: string): string => {
    const config = getLocaleConfig(locale);
    const strategy = formatStrategies.find((s) => num < s.maxValue);

    if (!strategy) return `${num}`;

    const rounded = roundToSignificantDigits(num, strategy.significantDigits);

    return strategy.format(rounded, config);
};
